import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, getCollectionName } from './firebase';
import { collection, getDocs, query, where, doc, updateDoc } from './firestore-compat';
import GestaoOcorrencias from './GestaoOcorrencias';

export default function OcorrenciaSetores() {
  const [userData, setUserData] = useState(null);
  const [setoresDisponiveis, setSetoresDisponiveis] = useState([]);
  const [setoresSelecionados, setSetoresSelecionados] = useState([]);
  const [salvandoSetor, setSalvandoSetor] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);
  const [error, setError] = useState(null);
  const [alterandoSetor, setAlterandoSetor] = useState(false);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError('Usuário não autenticado.');
      setLoadingUser(false);
      return;
    }
    getDocs(query(collection(db, 'users'), where('email', '==', currentUser.email)))
      .then(snap => {
        if (!snap.empty) setUserData({ id: snap.docs[0].id, ...snap.docs[0].data() });
        else setError('Usuário não encontrado na base de dados.');
      })
      .catch(() => setError('Erro ao carregar dados do usuário.'))
      .finally(() => setLoadingUser(false));
  }, []);

  useEffect(() => {
    getDocs(collection(db, getCollectionName('setores_config')))
      .then(snap => setSetoresDisponiveis(
        snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.name)
      ))
      .catch(() => {});
  }, []);

  // Setores ativos do usuário (suporta array novo e string legada)
  const setoresDoUsuario = useMemo(() => {
    if (!userData) return [];
    if (Array.isArray(userData.setores) && userData.setores.length > 0) return userData.setores;
    if (userData.setor) return [userData.setor];
    return [];
  }, [userData]);

  const toggleSetor = (nome) => {
    setSetoresSelecionados(prev =>
      prev.includes(nome) ? prev.filter(s => s !== nome) : [...prev, nome]
    );
  };

  const handleSalvarSetores = async () => {
    if (setoresSelecionados.length === 0 || !userData?.id) return;
    setSalvandoSetor(true);
    try {
      await updateDoc(doc(db, getCollectionName('users'), userData.id), { setores: setoresSelecionados });
      setUserData(prev => ({ ...prev, setores: setoresSelecionados }));
    } catch (err) {
      setError('Erro ao salvar setores: ' + err.message);
    } finally {
      setSalvandoSetor(false);
    }
  };

  if (loadingUser) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#64748b', fontFamily: "'Inter', sans-serif" }}>
        ⏳ Carregando...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#ef4444', fontFamily: "'Inter', sans-serif" }}>
        ⚠️ {error}
      </div>
    );
  }

  // Usuário com setor(es) configurado(s) → renderiza GestaoOcorrencias filtrada
  if (setoresDoUsuario.length > 0 && !alterandoSetor) {
    return (
      <div style={{ position: 'relative' }}>
        <GestaoOcorrencias setorFilter={setoresDoUsuario} sectorNome={setoresDoUsuario.join(' + ')} />
      </div>
    );
  }

  // Modo troca de setor
  if (alterandoSetor) {
    const handleSalvar = async () => {
      if (setoresSelecionados.length === 0 || !userData?.id) return;
      setSalvandoSetor(true);
      try {
        await updateDoc(doc(db, getCollectionName('users'), userData.id), { setores: setoresSelecionados });
        setUserData(prev => ({ ...prev, setores: setoresSelecionados }));
        setAlterandoSetor(false);
      } catch (err) {
        setError('Erro ao salvar setores: ' + err.message);
      } finally {
        setSalvandoSetor(false);
      }
    };
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '36px 40px', maxWidth: 480, width: '100%', boxShadow: '0 4px 32px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: '3rem', marginBottom: 12, textAlign: 'center' }}>🔄</div>
          <h3 style={{ margin: '0 0 8px', color: '#1e293b', fontSize: '1.1rem', textAlign: 'center' }}>Alterar setor(es)</h3>
          <p style={{ color: '#64748b', fontSize: '0.88rem', margin: '0 0 20px', lineHeight: 1.6, textAlign: 'center' }}>
            Atual: <strong>{userData?.setores?.join(', ') || userData?.setor || '—'}</strong>
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {setoresDisponiveis.map(s => {
              const ativo = setoresSelecionados.includes(s.name);
              return (
                <button key={s.id} onClick={() => setSetoresSelecionados(prev => ativo ? prev.filter(x => x !== s.name) : [...prev, s.name])}
                  style={{ padding: '10px 16px', borderRadius: 8, border: `2px solid ${ativo ? '#6366f1' : '#e2e8f0'}`, background: ativo ? '#eef2ff' : '#fff', color: ativo ? '#4f46e5' : '#475569', fontWeight: ativo ? 700 : 500, fontSize: '0.93rem', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.15s' }}>
                  <span style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${ativo ? '#6366f1' : '#cbd5e1'}`, background: ativo ? '#6366f1' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.75rem', color: '#fff' }}>
                    {ativo ? '✓' : ''}
                  </span>
                  {s.name}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setAlterandoSetor(false)} style={{ flex: 1, padding: '11px', borderRadius: 8, background: '#f1f5f9', color: '#475569', border: 'none', fontWeight: 600, fontSize: '0.93rem', cursor: 'pointer' }}>
              Cancelar
            </button>
            <button onClick={handleSalvar} disabled={setoresSelecionados.length === 0 || salvandoSetor}
              style={{ flex: 2, padding: '11px', borderRadius: 8, background: setoresSelecionados.length > 0 ? '#6366f1' : '#e2e8f0', color: setoresSelecionados.length > 0 ? '#fff' : '#94a3b8', border: 'none', fontWeight: 700, fontSize: '0.93rem', cursor: setoresSelecionados.length > 0 ? 'pointer' : 'not-allowed' }}>
              {salvandoSetor ? '⏳ Salvando...' : '✅ Confirmar setor'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Sem setores ainda → exibe picker multi-select
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '36px 40px', maxWidth: 480, width: '100%', boxShadow: '0 4px 32px rgba(0,0,0,0.08)', border: '1px solid #f1f5f9' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12, textAlign: 'center' }}>🏥</div>
        <h3 style={{ margin: '0 0 8px', color: '#1e293b', fontSize: '1.1rem', textAlign: 'center' }}>Configure seus setores</h3>
        <p style={{ color: '#64748b', fontSize: '0.88rem', margin: '0 0 20px', lineHeight: 1.6, textAlign: 'center' }}>
          Selecione um ou mais setores aos quais você pertence. Você verá as ocorrências pendentes de todos eles.
        </p>
        {setoresDisponiveis.length > 0 ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {setoresDisponiveis.map(s => {
                const ativo = setoresSelecionados.includes(s.name);
                return (
                  <button
                    key={s.id}
                    onClick={() => toggleSetor(s.name)}
                    style={{
                      padding: '10px 16px', borderRadius: 8, border: `2px solid ${ativo ? '#6366f1' : '#e2e8f0'}`,
                      background: ativo ? '#eef2ff' : '#fff', color: ativo ? '#4f46e5' : '#475569',
                      fontWeight: ativo ? 700 : 500, fontSize: '0.93rem', cursor: 'pointer',
                      textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.15s',
                    }}
                  >
                    <span style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${ativo ? '#6366f1' : '#cbd5e1'}`, background: ativo ? '#6366f1' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.75rem', color: '#fff' }}>
                      {ativo ? '✓' : ''}
                    </span>
                    {s.name}
                  </button>
                );
              })}
            </div>
            {setoresSelecionados.length > 0 && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', fontSize: '0.8rem', color: '#15803d', marginBottom: 12 }}>
                ✅ Selecionados: {setoresSelecionados.join(', ')}
              </div>
            )}
            <button
              onClick={handleSalvarSetores}
              disabled={setoresSelecionados.length === 0 || salvandoSetor}
              style={{ width: '100%', padding: '11px', borderRadius: 8, background: setoresSelecionados.length > 0 ? '#6366f1' : '#e2e8f0', color: setoresSelecionados.length > 0 ? '#fff' : '#94a3b8', border: 'none', fontWeight: 700, fontSize: '0.93rem', cursor: setoresSelecionados.length > 0 ? 'pointer' : 'not-allowed', transition: 'all 0.15s' }}
            >
              {salvandoSetor ? '⏳ Salvando...' : `✅ Confirmar ${setoresSelecionados.length > 1 ? 'meus setores' : 'meu setor'}`}
            </button>
          </>
        ) : (
          <p style={{ color: '#f59e0b', fontWeight: 600, fontSize: '0.9rem', textAlign: 'center' }}>
            Nenhum setor cadastrado ainda. Contate o administrador para cadastrar os setores em Configurações.
          </p>
        )}
      </div>
    </div>
  );
}
